const moment = require("moment");
const momentDuration = require("moment-duration-format");
momentDuration(moment);

function dinamicStyle(color) {
    if (!color) {
        return {
            color: '#000',
            fontWeight: 'bold',
        };
    }

    const cleanHex = color.replace('#', '');

    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
    const textColor = luminance > 128 ? '#000' : '#fff';

    return {
        backgroundColor: color,
        color: textColor,
    };
}

/**
 * Retorna o horário de início e fim do expediente para o dia da semana
 * considerando horários diferentes para dias úteis e finais de semana.
 * Retrocompatível com schedule_start/schedule_end antigos.
 */
function getScheduleTimes(workSchedule, dayOfWeek) {
    const isWeekend = (dayOfWeek === 0 || dayOfWeek === 6);

    if (isWeekend && workSchedule?.weekend_start) {
        return {
            start: workSchedule.weekend_start,
            end: workSchedule.weekend_end ?? '23:59',
        };
    }

    if (workSchedule?.weekday_start) {
        return {
            start: workSchedule.weekday_start,
            end: workSchedule.weekday_end ?? '23:59',
        };
    }

    return {
        start: workSchedule?.schedule_start ?? '00:00',
        end: workSchedule?.schedule_end ?? '23:59',
    };
}

function calculateEffectiveWorkTime(start, end, workSchedule) {
    const scheduleDays = workSchedule?.schedule_days ?? [];

    if (!scheduleDays.length) {
        return end.diff(start, "seconds");
    }

    let totalSeconds = 0;
    let current = moment(start);

    while (current.isBefore(end)) {
        const dayOfWeek = current.day(); // 0 = Domingo, 6 = Sábado

        if (scheduleDays.includes(dayOfWeek)) {
            const times = getScheduleTimes(workSchedule, dayOfWeek);
            const [startHour, startMin] = times.start.split(':').map(Number);
            const [endHour, endMin] = times.end.split(':').map(Number);

            const dayStart = moment(current).set({ hour: startHour, minute: startMin, second: 0 });
            const dayEnd = moment(current).set({ hour: endHour, minute: endMin, second: 0 });

            const effectiveStart = current.isAfter(dayStart) ? current : dayStart;
            const effectiveEnd = end.isBefore(dayEnd) ? end : dayEnd;

            if (effectiveStart.isBefore(effectiveEnd) && effectiveStart.isBefore(dayEnd) && effectiveEnd.isAfter(dayStart)) {
                totalSeconds += effectiveEnd.diff(effectiveStart, "seconds");
            }
        }

        current = moment(current).add(1, 'day').startOf('day');
    }

    return totalSeconds;
}

/**
 * Calcula a data de expiração do SLA considerando o calendário de atendimento
 */
function calculateSLAExpiration(start, slaSeconds, workSchedule) {
    const scheduleDays = workSchedule?.schedule_days ?? [];

    if (!scheduleDays.length) {
        return moment(start).add(slaSeconds, "seconds");
    }

    let remainingSeconds = slaSeconds;
    let current = moment(start);

    const maxIterations = 365;
    let iterations = 0;

    while (remainingSeconds > 0 && iterations < maxIterations) {
        iterations++;
        const dayOfWeek = current.day();

        if (scheduleDays.includes(dayOfWeek)) {
            const times = getScheduleTimes(workSchedule, dayOfWeek);
            const [startHour, startMin] = times.start.split(':').map(Number);
            const [endHour, endMin] = times.end.split(':').map(Number);

            const dayStart = moment(current).set({ hour: startHour, minute: startMin, second: 0 });
            const dayEnd = moment(current).set({ hour: endHour, minute: endMin, second: 0 });

            if (current.isBefore(dayStart)) {
                current = moment(dayStart);
            }

            if (current.isSameOrAfter(dayStart) && current.isBefore(dayEnd)) {
                const availableSeconds = dayEnd.diff(current, "seconds");

                if (remainingSeconds <= availableSeconds) {
                    current = moment(current).add(remainingSeconds, "seconds");
                    remainingSeconds = 0;
                } else {
                    remainingSeconds -= availableSeconds;
                    current = moment(dayEnd);
                }
            }
        }

        if (remainingSeconds > 0) {
            current = moment(current).add(1, 'day').startOf('day');
        }
    }

    return current;
}

function handleTicketSLA(ticket) {
    try {
        if (!ticket) return "-";
        const now = moment();
        const start = moment(ticket.started_at);
        const hasSchedule = ticket.has_schedule ?? false;
        const workSchedule = ticket.work_schedule ?? {};

        // Calcula a data de expiração do SLA
        let expireAt;
        if (hasSchedule && workSchedule?.schedule_days?.length) {
            expireAt = calculateSLAExpiration(start, ticket.sla, workSchedule);
        } else {
            expireAt = moment(ticket.started_at).add(ticket.sla, "seconds");
        }

        // Tempo total de atendimento (efetivo, considerando calendário)
        const end = ticket.closed_at ? moment(ticket.closed_at) : now;
        let duration;
        if (hasSchedule && workSchedule?.schedule_days?.length) {
            duration = calculateEffectiveWorkTime(start, end, workSchedule);
        } else {
            duration = end.diff(start, "seconds");
        }
        const durationFormat = convertSecondsToTime(duration, false, true);

        // Verificar se SLA expirou
        const isExpired = now.isAfter(expireAt);

        // Calcular tempo de alerta
        const percent = ticket?.priority?.percent_alert ?? 0;
        const alertThreshold = Math.floor(ticket.sla * (percent / 100));

        let alertTime;
        if (hasSchedule && workSchedule?.schedule_days?.length) {
            alertTime = calculateSLAExpiration(start, alertThreshold, workSchedule);
        } else {
            alertTime = moment(ticket.started_at).add(alertThreshold, "seconds");
        }
        const isInAlert = now.isAfter(alertTime);

        if (ticket.closed_at) {
            // Ticket fechado
            const ticketClosedAt = moment(ticket.closed_at);
            const slaExpiredWhenClosed = ticketClosedAt.isAfter(expireAt);

            if (slaExpiredWhenClosed) {
                // SLA já estava expirado quando o ticket foi fechado
                let timeAfterExpiration;
                if (hasSchedule && workSchedule?.schedule_days?.length) {
                    timeAfterExpiration = calculateEffectiveWorkTime(expireAt, ticketClosedAt, workSchedule);
                } else {
                    timeAfterExpiration = ticketClosedAt.diff(expireAt, "seconds");
                }
                const timeAfterExpirationFormat = convertSecondsToTime(timeAfterExpiration, false, true);
                return ticketSpanSLA("expire", `Concluído em ${durationFormat} com SLA expirado à ${timeAfterExpirationFormat}`);
            } else {
                // SLA ainda não havia expirado quando o ticket foi fechado
                let timeLeftWhenClosed;
                if (hasSchedule && workSchedule?.schedule_days?.length) {
                    timeLeftWhenClosed = calculateEffectiveWorkTime(ticketClosedAt, expireAt, workSchedule);
                } else {
                    timeLeftWhenClosed = expireAt.diff(ticketClosedAt, "seconds");
                }
                const timeLeftFormat = convertSecondsToTime(timeLeftWhenClosed, false, true);
                return ticketSpanSLA("normal", `Concluído em ${durationFormat} faltando ${timeLeftFormat} para expirar`);
            }
        } else {
            // Ticket aberto
            if (isExpired) {
                let timeAfterExpiration;
                if (hasSchedule && workSchedule?.schedule_days?.length) {
                    timeAfterExpiration = calculateEffectiveWorkTime(expireAt, now, workSchedule);
                } else {
                    timeAfterExpiration = now.diff(expireAt, "seconds");
                }
                const timeAfterExpirationFormat = convertSecondsToTime(timeAfterExpiration, false, true);
                return ticketSpanSLA("expire", `Vencido à ${timeAfterExpirationFormat}`);
            } else if (isInAlert) {
                let timeLeft;
                if (hasSchedule && workSchedule?.schedule_days?.length) {
                    timeLeft = calculateEffectiveWorkTime(now, expireAt, workSchedule);
                } else {
                    timeLeft = expireAt.diff(now, "seconds");
                }
                const timeLeftFormat = convertSecondsToTime(timeLeft, false, true);
                return ticketSpanSLA("alert", `Próximo de vencer em ${timeLeftFormat}`);
            } else {
                let timeLeft;
                if (hasSchedule && workSchedule?.schedule_days?.length) {
                    timeLeft = calculateEffectiveWorkTime(now, expireAt, workSchedule);
                } else {
                    timeLeft = expireAt.diff(now, "seconds");
                }
                const timeLeftFormat = convertSecondsToTime(timeLeft, false, true);
                return ticketSpanSLA("normal", `Vence em ${timeLeftFormat}`);
            }
        }
    } catch (error) {
        console.error(error);
        return "-";
    }
}

/**
 * Retorna a data de vencimento do SLA do ticket no formato "DD/MM/YYYY HH:mm:ss"
 */
function handleTicketSLAExpirationDate(ticket) {
    try {
        if (!ticket) return "-";
        const start = moment(ticket.started_at);
        const hasSchedule = ticket.has_schedule ?? false;
        const workSchedule = ticket.work_schedule ?? {};

        let expireAt;
        if (hasSchedule && workSchedule?.schedule_days?.length) {
            expireAt = calculateSLAExpiration(start, ticket.sla, workSchedule);
        } else {
            expireAt = moment(ticket.started_at).add(ticket.sla, "seconds");
        }

        return expireAt.format("DD/MM/YYYY HH:mm:ss");
    } catch (error) {
        console.error(error);
        return "-";
    }
}

function ticketSpanSLA(type, text) {
    if (type == "expire") {
        return `<span style='color: #d32f2f; font-weight: bold;'>🚫 ${text}</span>`;
    } else if (type == "alert") {
        return `<span style='color: #ca6702; font-weight: bold;'>⚠️ ${text}</span>`;
    } else {
        return `<span style='color: #4caf50; font-weight: bold;'>✅ ${text}</span>`;
    }
}

function solicitationSpanSLA(type, text) {
    if (type == "expire") {
        return `<span style='color: #d32f2f; font-weight: bold;'>🔴 ${text}</span>`;
    } else if (type == "warning") {
        return `<span style='color: #ffb703; font-weight: bold;'>🟡 ${text}</span>`;
    } else if (type == "alert") {
        return `<span style='color: #ca4102ff; font-weight: bold;'>🟠 ${text}</span>`;
    } else {
        return `<span style='color: #4caf50; font-weight: bold;'>🟢 ${text}</span>`;
    }
}

function formTelefone(tel) {
    if ((tel.length == 10) || (tel.length == 11)) {
        const v1 = tel.replace(/\D/g, "");
        const v2 = v1.replace(/^(\d{2})(\d)/g, "($1) $2");
        const v3 = v2.replace(/(\d)(\d{4})$/, "$1-$2");

        return v3;
    }
    tel = tel.replace(/\D/g, "")
    tel = tel.replace(/^(\d)/, "+$1")
    tel = tel.replace(/(.{3})(\d)/, "$1($2")
    tel = tel.replace(/(.{6})(\d)/, "$1)$2")
    if (tel.length == 12) {
        tel = tel.replace(/(.{1})$/, "-$1")
    }
    else if (tel.length == 13) {
        tel = tel.replace(/(.{2})$/, "-$1")
    }
    else if (tel.length == 14) {
        tel = tel.replace(/(.{3})$/, "-$1")
    }
    else if (tel.length == 15) {
        tel = tel.replace(/(.{4})$/, "-$1")
    }
    else if (tel.length > 15) {
        tel = tel.replace(/(.{4})$/, "-$1")
    }
    tel = tel.replace("(", " (");
    tel = tel.replace(")", ") ");

    return tel;
}

function formatDate(date) {
    if (!date) return '';
    return moment(date).format('DD/MM/YYYY HH:mm:ss');
}

function convertSecondsToTime(seconds, showSec = true, humanize = false) {
    if (humanize) {
        const formatedHours = Math.floor(seconds / 3600);
        const formatedMinutes = Math.floor((seconds % 3600) / 60);
        const formatedSeconds = seconds % 60;

        let parts = [];
        if (formatedHours > 0) {
            parts.push(`${formatedHours}h`);
            parts.push(`${formatedMinutes.toString().padStart(2, '0')}min`);
        } else if (formatedMinutes > 0) {
            parts.push(`${formatedMinutes}min`);
        } else {
            parts.push(`${formatedSeconds}sec`);
        }

        return parts.join(" ");
    } else {
        if (seconds >= 86400) {
            return moment.duration(seconds, "seconds").format(showSec ? "h:mm:ss" : "h:mm").replace(',', '.')
        }
        return moment.utc(seconds * 1000).format(showSec ? "HH:mm:ss" : "HH:mm");
    }
}

function isTimeWithinPercentage(timeSec, startDate, percentage) {
    const futureTime = moment(startDate).add(timeSec, 'seconds');
    const now = moment();
    const totalDuration = moment.duration(futureTime.diff(moment(startDate))).asSeconds();
    const elapsedDuration = moment.duration(now.diff(moment(startDate))).asSeconds();
    const elapsedPercentage = (elapsedDuration / totalDuration) * 100;
    return elapsedPercentage >= percentage;
}

function getFileIcon(fileName) {
    const extension = fileName.split('.').pop().toLowerCase();
    const iconMap = {
        // Imagens
        'jpg': 'fas fa-file-image',
        'jpeg': 'fas fa-file-image',
        'png': 'fas fa-file-image',
        'gif': 'fas fa-file-image',
        'bmp': 'fas fa-file-image',
        'svg': 'fas fa-file-image',
        // Documentos
        'pdf': 'fas fa-file-pdf',
        'doc': 'fas fa-file-word',
        'docx': 'fas fa-file-word',
        'xls': 'fas fa-file-excel',
        'xlsx': 'fas fa-file-excel',
        'ppt': 'fas fa-file-powerpoint',
        'pptx': 'fas fa-file-powerpoint',
        'txt': 'fas fa-file-alt',
        // Áudio
        'mp3': 'fas fa-file-audio',
        'wav': 'fas fa-file-audio',
        'ogg': 'fas fa-file-audio',
        // Vídeo
        'mp4': 'fas fa-file-video',
        'avi': 'fas fa-file-video',
        'mov': 'fas fa-file-video',
        'mkv': 'fas fa-file-video',
        // Arquivos compactados
        'zip': 'fas fa-file-archive',
        'rar': 'fas fa-file-archive',
        '7z': 'fas fa-file-archive',
        // Código
        'html': 'fas fa-file-code',
        'css': 'fas fa-file-code',
        'js': 'fas fa-file-code',
        'json': 'fas fa-file-code',
        'xml': 'fas fa-file-code',
        'php': 'fas fa-file-code',
        'py': 'fas fa-file-code',
        // Padrão
        default: 'fas fa-file-alt'
    };

    return iconMap[extension] || iconMap.default;
}

function textSizeLimit(text = "", size = 100) {
    if (typeof text != "string" || typeof size != "number") return text;
    const pontoIndex = text.lastIndexOf('.');

    if (pontoIndex === -1) {
        return text.substring(0, size);
    }

    const extensao = text.substring(pontoIndex);
    const nome = text.substring(0, pontoIndex);

    const tamanhoDisponivel = size - extensao.length;

    if (tamanhoDisponivel <= 0) {
        return extensao.substring(0, size);
    }

    return nome.substring(0, tamanhoDisponivel) + extensao;
}

function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function handleCreatedAt(date, immutable = false) {
    if (immutable) return "-";
    return moment(date).format('DD/MM/YYYY HH:mm:ss');
}

function handleSolicitationSLA(item, att = false, html = true) {
    try {
        if (!item) return "-";
        const sla = att ? item?.sla_att : item?.sla;
        if (!sla) return "-";
        const timeEnd = att ? item?.attendance_at : item?.closed_at;

        // Validação adicional para attendance_at quando att = true
        if (att && !item?.attendance_at) {
            // Se está buscando SLA de atendimento mas não tem data de atendimento, continua calculando como aberto
        }

        const now = moment();
        const start = moment(item.created_at);
        const expireAt = moment(item.created_at).add(sla, "seconds");

        // Tempo total de atendimento
        const end = timeEnd ? moment(timeEnd) : now;
        const duration = end.diff(start, "seconds");
        const durationFormat = convertSecondsToTime(duration, false, true);

        // Calcular tempo de aviso quando o tempo passar de 50%
        const warningThreshold = sla * (50 / 100);
        const warningTime = moment(item.created_at).add(warningThreshold, "seconds");

        // Calcular tempo de alerta quando o tempo passar de 75%
        const alertThreshold = sla * (75 / 100);
        const alertTime = moment(item.created_at).add(alertThreshold, "seconds");

        if (timeEnd) {
            // item fechado
            const itemClosedAt = moment(timeEnd);
            const slaExpiredWhenClosed = itemClosedAt > expireAt;

            if (slaExpiredWhenClosed) {
                // SLA já estava expirado quando o item foi fechado
                const timeAfterExpiration = itemClosedAt.diff(expireAt, "seconds");
                const timeAfterExpirationFormat = convertSecondsToTime(timeAfterExpiration, false, true);
                if (html) {
                    return solicitationSpanSLA("expire", `Concluído em ${durationFormat} com SLA expirado à ${timeAfterExpirationFormat}`);
                } else {
                    return `Concluído em ${durationFormat} com SLA expirado à ${timeAfterExpirationFormat}`;
                }
            } else {
                // SLA ainda não havia expirado quando o item foi fechado
                const timeLeftWhenClosed = expireAt.diff(itemClosedAt, "seconds");
                const timeLeftFormat = convertSecondsToTime(timeLeftWhenClosed, false, true);
                if (html) {
                    return solicitationSpanSLA("normal", `Concluído em ${durationFormat} faltando ${timeLeftFormat} para expirar`);
                } else {
                    return `Concluído em ${durationFormat} faltando ${timeLeftFormat} para expirar`;
                }
            }
        } else {
            if (item.status === 'Fechado') {
                if (html) {
                    return solicitationSpanSLA("normal", `Concluído`);
                } else {
                    return `Concluído`;
                }
            }
            // item aberto - verificação na ordem correta: expirado → alerta → warning → normal
            if (now > expireAt) {
                // SLA expirado
                const timeAfterExpiration = now.diff(expireAt, "seconds");
                const timeAfterExpirationFormat = convertSecondsToTime(timeAfterExpiration, false, true);
                if (html) {
                    return solicitationSpanSLA("expire", `Vencido à ${timeAfterExpirationFormat}`);
                } else {
                    return `Vencido à ${timeAfterExpirationFormat}`;
                }
            } else if (now > alertTime) {
                // SLA em alerta (75% do tempo passou)
                const timeLeft = expireAt.diff(now, "seconds");
                const timeLeftFormat = convertSecondsToTime(timeLeft, false, true);
                if (html) {
                    return solicitationSpanSLA("alert", `Próximo de vencer em ${timeLeftFormat}`);
                } else {
                    return `Próximo de vencer em ${timeLeftFormat}`;
                }
            } else if (now > warningTime) {
                // SLA em aviso (50% do tempo passou)
                const timeLeft = expireAt.diff(now, "seconds");
                const timeLeftFormat = convertSecondsToTime(timeLeft, false, true);
                if (html) {
                    return solicitationSpanSLA("warning", `Próximo de vencer em ${timeLeftFormat}`);
                } else {
                    return `Próximo de vencer em ${timeLeftFormat}`;
                }
            } else {
                // SLA normal (menos de 50% do tempo passou)
                const timeLeft = expireAt.diff(now, "seconds");
                const timeLeftFormat = convertSecondsToTime(timeLeft, false, true);
                if (html) {
                    return solicitationSpanSLA("normal", `Vence em ${timeLeftFormat}`);
                } else {
                    return `Vence em ${timeLeftFormat}`;
                }
            }
        }
    } catch (error) {
        console.error(error);
        return "-";
    }
}

function dateSolicitationSLA(item, firstAtt = false) {
    if (!item) return "";
    const sla = firstAtt ? item?.sla_att : item?.sla;
    if (!sla) return "";
    const start = moment(item.created_at);
    const expireAt = moment(item.created_at).add(sla, "seconds");
    return expireAt.format('DD/MM/YYYY HH:mm:ss');
}

function camelCaseString(text) {
    try {
        return `${text[0].toUpperCase()}${text.slice(1)}`;
    } catch {
        return text
    }
}

function selectTimelineIcon(text) {
    text = text.toLowerCase();
    if (text.includes("risco") || text.includes("próximo de vencer")) return "⚠️";
    if (text.includes("vencido") || text.includes("expirado")) return "‼️";
    if (text.includes("falha") ||
        text.includes("erro") ||
        text.includes("bloqueado")
    ) return "🚫";
    if (text.includes("finalizado") ||
        text.includes("fechado") ||
        text.includes("concluído") ||
        text.includes("concluido")
    ) return "✅";
    if (text.includes("grupo e usuário alterados!")) return "🔄";
    if (text.includes("tempo")) return "🕓";
    if (text.includes("prioridade")) return "⁉️";
    if (text.includes("título")) return "🖊️";
    if (text.includes("descrição")) return "📖";
    if (text.includes("criado")) return "🎉";
    if (text.includes("coment")) return "💬";
    if (text.includes("atendente")) return "🧑🏻";
    if (text.includes("transferido")) return "🔄";
    if (text.includes("reaberto")) return "↩️";
    if (text.includes("alterado")) return "🔶";
    if (text.includes("alterada")) return "🔷";
    if (text.includes("mail")) return "📤";
    if (text.includes("tarefa")) return "📝";
    return "⭐";
}

module.exports = {
    dinamicStyle,
    handleTicketSLA,
    formTelefone,
    formatDate,
    convertSecondsToTime,
    handleTicketSLAExpirationDate,
    isTimeWithinPercentage,
    getFileIcon,
    textSizeLimit,
    formatFileSize,
    handleCreatedAt,
    handleSolicitationSLA,
    camelCaseString,
    selectTimelineIcon,
    dateSolicitationSLA,
}